import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import OnboardingWizard from '@/pages/website-builder/OnboardingWizard';
import TrialBanner from '@/pages/website-builder/TrialBanner';
import CollabBanners from '@/pages/website-builder/CollabBanners';
import SqsShell from '@/pages/website-builder/sqs/SqsShell';
import '@/pages/website-builder/wb-drag-stability.css';
import { Palette, ShieldCheck, ArrowLeft, X } from 'lucide-react';
import {
  usePlanStatus, PromoBanner, TrialCountdown, PlanPickerDialog,
} from '@/pages/website-builder/TrialPlanWidgets';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function WebsiteBuilderLayout() {
  const { user, getAuthHeaders } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [site, setSite] = useState(null);
  const [showDesignerPromo, setShowDesignerPromo] = useState(false);
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const { data: planStatus, reload: reloadPlan } = usePlanStatus();

  // Hide the sidebar when in full-screen content editor mode
  const isEditor = location.pathname === '/website-builder/content';
  const isFullscreen = isEditor && location.search.includes('fullscreen=1');

  useEffect(() => {
    if (user) fetchSiteStatus();
  }, [user]);

  const fetchSiteStatus = async () => {
    try {
      const response = await fetch(`${API}/builder/my-site`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setSite(data);
        if (!data.onboarding_completed) {
          setShowOnboarding(true);
        } else {
          const promoShown = localStorage.getItem('designer_promo_shown')
            || localStorage.getItem('wb.designer_promo_dismissed');
          if (!promoShown) setShowDesignerPromo(true);
        }
      } else if (response.status === 404) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error('Failed to fetch site status:', error);
    }
  };

  // Iter 198j — Escape dismisses the designer promo just like the X /
  // backdrop-click paths, so vendors are never stuck seeing an upsell.
  useEffect(() => {
    if (!showDesignerPromo) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setShowDesignerPromo(false);
        localStorage.setItem('wb.designer_promo_dismissed', Date.now().toString());
        localStorage.setItem('designer_promo_shown', Date.now().toString());
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showDesignerPromo]);

  return (
    <div className="en-purple" data-testid="wb-builder-root">
      {showOnboarding && <OnboardingWizard onComplete={() => { setShowOnboarding(false); fetchSiteStatus(); }} />}

      {/* Iter 175i — Master impersonation banner. Shown when the master
          has opened this client's site via the "Edit Site" flow. */}
      <MasterImpersonationBanner navigate={navigate} />

      <SqsShell
        hideSidebar={isFullscreen}
        sidebarSlot={!isFullscreen && (
          <TrialCountdown
            planStatus={planStatus}
            onPickPlan={() => setShowPlanPicker(true)}
            inline
          />
        )}
      >
        {!isFullscreen && <PromoBanner planStatus={planStatus} onPickPlan={() => setShowPlanPicker(true)} />}
        {!isFullscreen && <TrialBanner />}
        {!isFullscreen && <CollabBanners />}
        <Outlet context={{ site, refreshSite: fetchSiteStatus }} />
      </SqsShell>

      <PlanPickerDialog open={showPlanPicker} onClose={() => setShowPlanPicker(false)} planStatus={planStatus} onPicked={reloadPlan} />

      {showDesignerPromo && (
        <div
          className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => {
            setShowDesignerPromo(false);
            localStorage.setItem('wb.designer_promo_dismissed', Date.now().toString());
            localStorage.setItem('designer_promo_shown', Date.now().toString());
          }}
          data-testid="designer-promo-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[#15152E] border border-white/10 rounded-2xl p-6 max-w-md w-full text-center"
          >
            {/* Iter 198j — explicit X close so vendors aren't trapped into
                picking one of the two upsell CTAs to dismiss. */}
            <button
              onClick={() => {
                setShowDesignerPromo(false);
                localStorage.setItem('wb.designer_promo_dismissed', Date.now().toString());
                localStorage.setItem('designer_promo_shown', Date.now().toString());
              }}
              className="absolute top-3 right-3 w-8 h-8 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06] flex items-center justify-center"
              aria-label="Close"
              data-testid="close-designer-promo"
            >
              <X size={16} />
            </button>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#8B5CF6] to-[#3B82F6] flex items-center justify-center mx-auto mb-4">
              <Palette size={28} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-white font-syne mb-2">Need a Professional Touch?</h2>
            <p className="text-[#9CA3AF] text-sm mb-4">
              Hire a professional website designer to create your dream website for you.
            </p>
            <div className="bg-gradient-to-r from-[#8B5CF6]/10 to-[#3B82F6]/10 border border-[#8B5CF6]/20 rounded-xl p-4 mb-4">
              <p className="text-[#64748B] text-sm line-through">$750</p>
              <p className="text-3xl font-bold text-white font-syne">$375</p>
              <p className="text-green-400 text-sm font-bold">50% OFF — Limited Time!</p>
              <p className="text-[#64748B] text-[10px] mt-1">This offer expires in 24 hours</p>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => {
                  localStorage.setItem('wb.designer_promo_dismissed', Date.now().toString());
                  localStorage.setItem('designer_promo_shown', Date.now().toString());
                  navigate('/website-builder/hire-designer');
                  setShowDesignerPromo(false);
                }}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-bold text-sm"
                data-testid="hire-designer-promo-btn"
              >
                Hire a Designer — $375
              </button>
              <button
                onClick={() => {
                  setShowDesignerPromo(false);
                  localStorage.setItem('wb.designer_promo_dismissed', Date.now().toString());
                  localStorage.setItem('designer_promo_shown', Date.now().toString());
                }}
                className="w-full text-[#9CA3AF] text-sm hover:text-white transition-colors"
                data-testid="skip-designer-promo"
              >
                I'll set it up myself
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}


// Iter 175i — Master impersonation banner.
// Reads `masterImpersonation` from sessionStorage. If present, renders a
// sticky violet bar at the top of the builder identifying the master as
// the active editor + a "Back to Master Dashboard" button that restores
// the original master token and returns to /master/sites.
function MasterImpersonationBanner({ navigate }) {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('masterImpersonation');
      if (raw) setMeta(JSON.parse(raw));
    } catch (e) {
      setMeta(null);
    }
  }, []);

  const exit = () => {
    try {
      const raw = sessionStorage.getItem('masterImpersonation');
      if (raw) {
        const data = JSON.parse(raw);
        if (data.original_master_token) {
          localStorage.setItem('token', data.original_master_token);
        } else {
          localStorage.removeItem('token');
        }
        if (data.original_master_user) {
          localStorage.setItem('user', data.original_master_user);
        } else {
          localStorage.removeItem('user');
        }
      }
    } catch (e) {
      // best-effort restore
    }
    sessionStorage.removeItem('masterImpersonation');
    navigate('/master/sites');
    // Hard reload so the AuthContext picks up the restored token cleanly
    setTimeout(() => window.location.reload(), 50);
  };

  if (!meta) return null;
  return (
    <div
      className="sticky top-0 z-[450] flex items-center justify-between gap-3 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white shadow-lg"
      style={{ background: 'linear-gradient(90deg, #A21CAF, #6D28D9, #1D4ED8)' }}
      data-testid="master-impersonation-banner"
    >
      <button
        onClick={exit}
        className="flex items-center gap-2 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all"
        data-testid="master-impersonation-exit"
      >
        <ArrowLeft size={12} /> Back to Master Dashboard
      </button>
      <div className="flex items-center gap-2 text-white/95 truncate">
        <ShieldCheck size={12} />
        <span className="hidden sm:inline">Editing as Master ·</span>
        <span className="font-mono normal-case tracking-normal text-[11px] text-white/90 truncate">
          {meta.site_name || meta.site_id}
        </span>
        <span className="hidden md:inline text-white/55">·</span>
        <span className="hidden md:inline text-white/55 normal-case tracking-normal text-[11px] truncate">
          owner: {meta?.user?.email}
        </span>
      </div>
      <span className="hidden lg:inline text-[10px] text-white/70 normal-case tracking-normal">
        4-hour session
      </span>
    </div>
  );
}
